import type { FC, FormEvent, ReactNode } from 'react'
import type { CompanyApplication, CompanyField } from '@/lib/applications'
import type { FieldErrors } from '@/lib/validate'

import { useRef, useState } from 'react'
import { Button } from '@/components/generals/button-link'
import { FormSuccess, Honeypot } from '@/components/forms/application-shell'
import { SelectField, TextAreaField, TextField } from '@/components/forms/fields'
import { STAGES, TIMELINES, validateCompanyApplication } from '@/lib/applications'
import { routes } from '@/constants/site'

type Status = 'idle' | 'sending' | 'sent'

const EMPTY: CompanyApplication = {
    fullName: '',
    email: '',
    company: '',
    website: '',
    roleTitle: '',
    stage: '',
    timeline: '',
    notes: ''
}

const FIELD_ORDER: CompanyField[] = ['fullName', 'email', 'company', 'website', 'roleTitle', 'stage', 'timeline', 'notes']

export const CompanyForm: FC = (): ReactNode => {
    const [values, setValues] = useState<CompanyApplication>(EMPTY)
    const [errors, setErrors] = useState<FieldErrors<CompanyField>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [formError, setFormError] = useState('')
    const [nickname, setNickname] = useState('')
    const formRef = useRef<HTMLFormElement>(null)

    const update = (field: CompanyField) => (value: string) => {
        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const focusFirstError = (found: FieldErrors<CompanyField>) => {
        const first = FIELD_ORDER.find(field => found[field])

        if (first) formRef.current?.querySelector<HTMLElement>(`#${first}`)?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'sending') return

        const { errors: found } = validateCompanyApplication({ ...values })

        if (Object.keys(found).length > 0) {
            setErrors(found)
            setFormError('Please check the highlighted fields.')
            focusFirstError(found)

            return
        }

        setStatus('sending')
        setFormError('')

        try {
            const response = await fetch('/api/applications/company', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, nickname })
            })
            const result = await response.json().catch(() => null) as { message?: string, errors?: FieldErrors<CompanyField> } | null

            if (!response.ok) {
                if (result?.errors) {
                    setErrors(result.errors)
                    focusFirstError(result.errors)
                }

                setFormError(result?.message ?? 'Something went wrong. Please try again.')
                setStatus('idle')

                return
            }

            setStatus('sent')
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        catch {
            setFormError('We could not reach our server. Check your connection and try again.')
            setStatus('idle')
        }
    }

    if (status === 'sent') {
        const firstName = values.fullName.split(' ')[0]

        return (
            <FormSuccess
                title={`Thank you, ${firstName}. We'll be in touch.`}
                body={`Someone from our team will reply to ${values.email} within two working days to set up a short call about the ${values.roleTitle} role.`}
                primary={{ label: 'See recent placements', href: routes.placements }}
                secondary={{ label: 'Back to home', href: routes.home }}
            />
        )
    }

    return (
        <form ref={formRef} noValidate onSubmit={handleSubmit} className='relative flex flex-col gap-7' aria-describedby={formError ? 'company-form-error' : undefined}>
            <Honeypot value={nickname} onChange={setNickname} />

            <div className='grid gap-7 sm:grid-cols-2 sm:gap-5'>
                <TextField id='fullName' label='Your name' autoComplete='name' value={values.fullName} onChange={event => update('fullName')(event.target.value)} error={errors.fullName} />
                <TextField id='email' label='Work email' type='email' autoComplete='email' value={values.email} onChange={event => update('email')(event.target.value)} error={errors.email} />
            </div>

            <div className='grid gap-7 sm:grid-cols-2 sm:gap-5'>
                <TextField id='company' label='Company' autoComplete='organization' value={values.company} onChange={event => update('company')(event.target.value)} error={errors.company} />
                <TextField id='website' label='Company website' optional inputMode='url' autoComplete='url' placeholder='yourcompany.com' value={values.website} onChange={event => update('website')(event.target.value)} error={errors.website} />
            </div>

            <TextField
                id='roleTitle'
                label='Who are you hiring?'
                placeholder='Senior Product Designer'
                value={values.roleTitle}
                onChange={event => update('roleTitle')(event.target.value)}
                error={errors.roleTitle}
            />

            <div className='grid gap-7 sm:grid-cols-2 sm:gap-5'>
                <SelectField id='stage' label='Company stage' options={STAGES} value={values.stage} onChange={event => update('stage')(event.target.value)} error={errors.stage} />
                <SelectField id='timeline' label='When should they start?' options={TIMELINES} value={values.timeline} onChange={event => update('timeline')(event.target.value)} error={errors.timeline} />
            </div>

            <TextAreaField
                id='notes'
                label='Anything we should know?'
                optional
                rows={5}
                hint='The team, the product, the kind of craft you value. A few lines is plenty.'
                value={values.notes}
                onChange={event => update('notes')(event.target.value)}
                error={errors.notes}
            />

            <div className='flex flex-col gap-4 border-t border-ink/15 pt-7 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-[22rem] text-[12.5px] leading-snug text-ink/55'>
                    No cost until you hire. We use your details only to reply to this request.
                </p>

                <Button type='submit' size='lg' arrow disabled={status === 'sending'} className='shrink-0'>
                    {status === 'sending' ? 'Sending' : 'Send to our team'}
                </Button>
            </div>

            <p id='company-form-error' role='alert' className='-mt-3 min-h-[1.25rem] text-[13px] text-clay-deep'>
                {formError}
            </p>
        </form>
    )
}
